// Helpers de exibição de identidade do usuário.
//
// IMPORTANTE — feature de inclusão (LGBTQ+):
// quando `socialName` está preenchido, ele SUBSTITUI o `name` em TUDO que é
// exibido pra outros usuários: chat, recibos de pagamento, perfis, menus,
// mensagens. NUNCA expor `name` original em UI quando socialName existe.
//
// `initialsOverride` deixa o usuário escolher iniciais customizadas (máx 4
// chars) — ex: "LL" pra "Luís Lourenço Moraes" em vez do auto "LM".

export interface DisplayUser {
  name?: string | null;
  socialName?: string | null;
  initialsOverride?: string | null;
}

/** Nome efetivo pra exibição. Prefere socialName se preenchido. */
export function getDisplayName(u: DisplayUser | null | undefined): string {
  if (!u) return "";
  const social = (u.socialName ?? "").trim();
  if (social) return social;
  return (u.name ?? "").trim();
}

/** Auto-gera iniciais a partir do nome efetivo: primeiro + último token.
 *  "Luis Moraes" → "LM"; "Luís Lourenço Moraes" → "LM"; "Beyoncé" → "BE". */
function autoInitials(effectiveName: string): string {
  const cleaned = effectiveName
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip combining marks
    .replace(/[^A-Za-z\s'-]/g, "")
    .trim();
  if (!cleaned) return "??";
  const tokens = cleaned.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return "??";
  if (tokens.length === 1) {
    const t = tokens[0];
    const a = (t[0] ?? "").toUpperCase();
    const b = (t[1] ?? "").toUpperCase();
    return (a + b) || a || "??";
  }
  const first = tokens[0][0] ?? "";
  const last = tokens[tokens.length - 1][0] ?? "";
  return (first + last).toUpperCase() || "??";
}

/** Iniciais pra avatares/menus/chat. Respeita override se preenchido (máx 4
 *  chars). Caso contrário, deriva do nome efetivo (socialName > name). */
export function getInitials(u: DisplayUser | null | undefined): string {
  if (!u) return "??";
  const override = (u.initialsOverride ?? "").trim();
  if (override) return override.slice(0, 4).toUpperCase();
  return autoInitials(getDisplayName(u));
}
